// 认证四态判定（设计 v4 §2.2）。防御式纯函数：owner 优先于 marker ——
// marker + owner≠null 的任何残留/注入一律判 signedOut（登录墙挡住），
// 不变量靠判定处强制，不靠写入处承诺。marker 残留的清理由 engine 的
// adoption 完成路径负责，本模块保持纯（无写副作用），可安全用于
// __root.tsx 的 beforeLoad（同步；store 为同步水合的 localStorage persist）。

import { useEffect, useState } from 'react';
import { useStore } from '../store';
import { getLastSyncedUserId, outbox } from '../sync/outbox';
import { hasApiConfig } from '../sync/transport';
import { hasGuestMarker, isGuestMarkerDegraded } from './guest-marker';
import { getSessionSync, subscribeSession } from './session';
import { hasSupabaseConfig } from './supabase';

export type AuthState = 'authed' | 'signedOut' | 'guest' | 'unset';

// v4.1-4 错配构建守卫：Supabase 配了而 API base 没配 —— startSync 在 owner 判定前早退，
// owner 永不落定 → 登出后任何 marker（残留/注入）都会被判 guest → 设备门放行上一账号
// 数据。此类构建**永不返回 guest**（不只禁跳过按钮：注入 marker 也进不了 guest 态）。
function misconfiguredBuild(): boolean {
  return hasSupabaseConfig() && !hasApiConfig();
}

export function getAuthState(): AuthState {
  if (getSessionSync() != null) return 'authed';
  if (useStore.getState().ownerUserId != null) return 'signedOut';
  if (hasGuestMarker() && !isGuestMarkerDegraded() && !misconfiguredBuild()) return 'guest';
  return 'unset';
}

// marker 写入门：与 wipe 判据同源的正面证据（设计 v4 §2.3）。
// lastSyncedUserId 非空 = 上一会话属别的账号；outbox 非空 = 归属可疑的 op；
// 降级 = 证据链不可信。任一成立都不给「跳过」按钮 —— 杜绝降级假象下写 marker。
export function canShowSkip(): boolean {
  if (getAuthState() !== 'unset') return false;
  if (isGuestMarkerDegraded()) return false;
  if (misconfiguredBuild()) return false;
  return getLastSyncedUserId() == null && outbox.size() === 0;
}

// 响应式版本（quickbar / banner / 设置页用）。裸 getAuthState 是一次性读，
// 主窗口登录/adoption 清 marker 后另一窗口必须能收敛 —— 订阅 session + store +
// 跨窗口 storage 事件三个变化源。
export function useAuthState(): AuthState {
  const [state, setState] = useState<AuthState>(getAuthState);
  useEffect(() => {
    const recompute = () => setState(getAuthState());
    const unsubSession = subscribeSession(recompute);
    const unsubStore = useStore.subscribe(recompute);
    window.addEventListener('storage', recompute);
    recompute();
    return () => {
      unsubSession();
      unsubStore();
      window.removeEventListener('storage', recompute);
    };
  }, []);
  return state;
}
